import { CoralClientConfigNotFound } from './errors';
import { InterceptingHandler } from '../interceptors/intercepting-handler';
/**
 * A repository of instantiated CoralClients.
 *
 * Applications may want to create a singleton instance of this class as an easy
 * way to maintain a list of global interceptors, default qualifier, and the
 * list of available service configurations.
 */
export class CoralClientFactory {
    constructor(clientConfigFactories, qualifier, handler, globalInterceptors = []) {
        this.qualifier = qualifier;
        this.chain = new InterceptingHandler(handler, globalInterceptors);
        this.clientConfigFactoryRegistry = new Map(clientConfigFactories.map(factory => {
            return [factory.service, factory];
        }));
        this.clientRegistry = new Map;
    }
    /**
     * Create a new instance of a given CoralClient.
     *
     * Will always instantiate a new object, and will *not* persist that instance
     * in the client registry.
     */
    create(Constructor, qualifier = this.qualifier) {
        const configFactory = this.getConfigFactoryForService(Constructor.serviceName);
        return new Constructor(configFactory.getConfigForQualifier(qualifier), this.chain);
    }
    /**
     * Return an instance of the given CoralClient for a given (or default) qualifier.
     *
     * This will re-use an existing instance if one was already created for the
     * given CoralClient type with the given qualifier. If no such object exists,
     * a new client will be created and persisted for later re-use.
     */
    get(Constructor, qualifier = this.qualifier) {
        const qualifiedClientRegistry = this.clientRegistry.get(qualifier) || new Map;
        const registeredClient = qualifiedClientRegistry.get(Constructor);
        if (registeredClient) {
            return registeredClient;
        }
        const client = this.create(Constructor, qualifier);
        qualifiedClientRegistry.set(Constructor, client);
        this.clientRegistry.set(qualifier, qualifiedClientRegistry);
        return client;
    }
    getConfigFactoryForService(serviceName) {
        const configFactory = this.clientConfigFactoryRegistry.get(serviceName);
        if (!configFactory) {
            throw new CoralClientConfigNotFound(serviceName);
        }
        return configFactory;
    }
}
